/**
 * /sequences/:sequenceId — step editor. Cycle 13.
 */
import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";

interface SequenceRow {
  id: string;
  name: string;
  description: string | null;
  igAccountId: string;
  defaultMessageTag: string | null;
}

interface StepRow {
  id: string;
  position: number;
  delaySeconds: number;
  body: { text: string };
  messageTag: string | null;
}

const DELAY_PRESETS: { value: number; label: string }[] = [
  { value: 0, label: "Immediate" },
  { value: 60 * 60, label: "1 hour" },
  { value: 60 * 60 * 4, label: "4 hours" },
  { value: 60 * 60 * 24, label: "1 day" },
  { value: 60 * 60 * 24 * 3, label: "3 days" },
  { value: 60 * 60 * 24 * 7, label: "1 week" },
];

export const Route = createFileRoute("/_app/sequences/$sequenceId")({
  component: SequenceEditPage,
});

function SequenceEditPage() {
  const { sequenceId } = Route.useParams();
  const [seq, setSeq] = useState<SequenceRow | null>(null);
  const [steps, setSteps] = useState<StepRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newDelay, setNewDelay] = useState(60 * 60 * 24);
  const [newText, setNewText] = useState("");
  const [newTag, setNewTag] = useState<string>("");

  useEffect(() => {
    void load();
  }, [sequenceId]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<{ sequence: SequenceRow; steps: StepRow[] }>(`/sequences/${sequenceId}`);
      setSeq(r.data.sequence);
      setSteps(r.data.steps);
    } catch {
      setError("Sequence not found");
    } finally {
      setLoading(false);
    }
  }

  async function addStep(e: FormEvent) {
    e.preventDefault();
    if (!newText.trim() || adding) return;
    setAdding(true);
    setError(null);
    try {
      await api.post(`/sequences/${sequenceId}/steps`, {
        delaySeconds: newDelay,
        body: { text: newText.trim() },
        messageTag: newTag || null,
      });
      setNewText("");
      setNewDelay(60 * 60 * 24);
      setNewTag("");
      await load();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(msg ?? "Failed to add step");
    } finally {
      setAdding(false);
    }
  }

  async function deleteStep(stepId: string) {
    if (!confirm("Remove this step?")) return;
    try {
      await api.delete(`/sequences/${sequenceId}/steps/${stepId}`);
      await load();
    } catch {
      setError("Failed to remove step");
    }
  }

  async function moveStep(idx: number, dir: -1 | 1) {
    const target = idx + dir;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved!);
    try {
      await api.post(`/sequences/${sequenceId}/steps/reorder`, {
        stepIds: next.map((s) => s.id),
      });
      setSteps(next.map((s, i) => ({ ...s, position: i })));
    } catch {
      setError("Failed to reorder");
    }
  }

  if (loading) return <p>Loading…</p>;
  if (!seq) return <p>Not found.</p>;

  return (
    <section style={{ maxWidth: 720, margin: "0 auto" }}>
      <h1 style={{ margin: "0 0 8px", fontSize: 28 }}>{seq.name}</h1>
      {seq.description ? (
        <p style={{ color: "var(--muted)", marginTop: 0 }}>{seq.description}</p>
      ) : null}

      {error ? (
        <div className="brut-card" style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}>{error}</div>
      ) : null}

      <h2 style={{ fontSize: 18, marginTop: 24 }}>Steps</h2>
      {steps.length === 0 ? (
        <div className="brut-card">No steps yet — add one below.</div>
      ) : (
        <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
          {steps.map((s, i) => (
            <li key={s.id} className="brut-card" style={{ padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>
                    Step {i + 1} · wait {humanDelay(s.delaySeconds)}
                    {s.messageTag ? ` · tag ${s.messageTag}` : ""}
                  </div>
                  <pre style={{ margin: 0, fontFamily: "inherit", whiteSpace: "pre-wrap" }}>{s.body.text}</pre>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <button onClick={() => void moveStep(i, -1)} disabled={i === 0} className="brut-button is-secondary" style={{ fontSize: 11, padding: "2px 8px" }}>
                    ↑
                  </button>
                  <button onClick={() => void moveStep(i, 1)} disabled={i === steps.length - 1} className="brut-button is-secondary" style={{ fontSize: 11, padding: "2px 8px" }}>
                    ↓
                  </button>
                  <button onClick={() => void deleteStep(s.id)} className="brut-button is-danger" style={{ fontSize: 11, padding: "2px 8px" }}>
                    ✕
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      <form onSubmit={addStep} className="brut-card" style={{ padding: 14, marginTop: 16, display: "grid", gap: 10 }}>
        <strong>Add step</strong>
        <label className="brut-label">Wait before sending</label>
        <select
          value={newDelay}
          onChange={(e) => setNewDelay(Number(e.target.value))}
          className="brut-input"
        >
          {DELAY_PRESETS.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
        <label className="brut-label">Message body</label>
        <textarea
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          rows={4}
          maxLength={1024}
          className="brut-input"
          placeholder="What this step says."
          required
        />
        <label className="brut-label">Message tag (optional override)</label>
        <select
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          className="brut-input"
        >
          <option value="">(use sequence default)</option>
          <option value="ACCOUNT_UPDATE">ACCOUNT_UPDATE</option>
        </select>
        <button type="submit" disabled={!newText.trim() || adding} className="brut-button">
          {adding ? "Adding…" : "Add step"}
        </button>
      </form>
    </section>
  );
}

function humanDelay(seconds: number): string {
  if (seconds === 0) return "0 (immediate)";
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(seconds / 3600);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.round(seconds / 86400);
  return `${days}d`;
}
